import { onReady } from "@otto-ec/global-resources/dom";
import { toggle } from "@otto-ec/global-resources/toggle";
import { isOnexVariant } from "@otto-ec/global-resources/onex";


const LOCK_NAME = 'session-refresh-lock';

class RequestIdStorage {
    private static readonly KEY = 'fti-session-refresh-request-id';
    get(): string {
        const stored = localStorage.getItem(RequestIdStorage.KEY);
        if (stored) {
            return stored;
        }
        const newId = crypto.randomUUID();
        localStorage.setItem(RequestIdStorage.KEY, newId);
        return newId;
    }

    clear(): void {
        localStorage.removeItem(RequestIdStorage.KEY);
    }
}
class NextRefreshScheduler {
    private static readonly KEY = "fti-session-next-refresh";
    nextDue(): NextRefresh | null {
        const storedNextRefresh = localStorage.getItem(NextRefreshScheduler.KEY)
        if (storedNextRefresh) {
            try{
                const nextRefresh = JSON.parse(storedNextRefresh) as NextRefresh;
                const now = Date.now();
                if (now >= nextRefresh.refreshAfterTimestamp) {
                    return nextRefresh;
                }
            } catch (e) {
                console.error("Error while parsing next refresh from localStorage. Clearing next refresh.", e);
                this.clear()
            }
        }
        return null;
    }
    schedule(seconds: number, extendExpiration: boolean): void {
        const storedRefreshAfter = localStorage.getItem(NextRefreshScheduler.KEY)
        const currentNextRefresh = storedRefreshAfter ? JSON.parse(storedRefreshAfter) as NextRefresh : null;
        const now = Date.now();
        const requestedNextRefresh = now + (seconds * 1000);
        const isCloserToNow = currentNextRefresh == null || requestedNextRefresh < currentNextRefresh.refreshAfterTimestamp
        const needsExtendExpirationUpdate = currentNextRefresh == null || (extendExpiration && currentNextRefresh.extendExpiration == false)
        if (isCloserToNow || needsExtendExpirationUpdate) {
            const newNextRefresh: NextRefresh = {
                refreshAfterTimestamp: Math.min(requestedNextRefresh, currentNextRefresh?.refreshAfterTimestamp ?? Number.MAX_VALUE),
                extendExpiration: extendExpiration || (currentNextRefresh?.extendExpiration ?? false)
            }
            localStorage.setItem(NextRefreshScheduler.KEY, JSON.stringify(newNextRefresh));
        }
    }
    clear(): void {
        localStorage.removeItem(NextRefreshScheduler.KEY);
    }

}
const nextRequestScheduler = new NextRefreshScheduler();
const requestIdStorage = new RequestIdStorage();
interface Session {
    refreshAfterSeconds: number
}
async function refreshSessionByOrt(extendExpiration: boolean): Promise<Session | null> {
    const requestId = requestIdStorage.get();
    const url = `/session-maintenance/session/refresh?requestId=${requestId}&extendExpiration=${extendExpiration}`;

    const response = await fetch(url);

    if (response.ok) {
        requestIdStorage.clear();
        /*                                                       */
        const refreshAfter = response.headers.get('Refresh-After');
        if (refreshAfter) {
            return {
                refreshAfterSeconds: parseInt(refreshAfter, 10)
            }
        } else {
            return null;
        }
    } else {
        throw new Error(`Failed to refresh session: ${response.status} ${response.statusText}`);
    }
}

async function refreshIfNeeded() {
    const nextDue = nextRequestScheduler.nextDue()
    if (nextDue != null) {
        await navigator.locks.request(LOCK_NAME, async () => {
            const nextDueInsideLock = nextRequestScheduler.nextDue();
            if (nextDueInsideLock != null) {
                nextRequestScheduler.clear()
                try {
                    const session = await refreshSessionByOrt(nextDueInsideLock.extendExpiration);
                    if(session != null) {
                        nextRequestScheduler.schedule(session.refreshAfterSeconds, false);
                    }
                } catch (e) {
                    console.debug("Refresh session call failed. Scheduling retry.", e);
                    nextRequestScheduler.schedule(5, nextDueInsideLock.extendExpiration);
                }
            }
        });
    }
}

async function checkRefreshNeededPeriodically() {
    await refreshIfNeeded()
    /*                                   */
    setTimeout(checkRefreshNeededPeriodically, 1000)
}

interface NextRefresh {
    refreshAfterTimestamp: number,
    extendExpiration: boolean
}
async function refreshByOrt() {
    await navigator.locks.request(LOCK_NAME, () => nextRequestScheduler.schedule(0, true));
    await checkRefreshNeededPeriodically();
}

async function refreshByOwt() {
    await fetch("/session-maintenance/refresh");
}

export async function initRefresh() {
    if (toggle.get("fti_enable_session_refresh_by_ort") || isOnexVariant("E3698", "E3698B")) {
        await refreshByOrt()
    } else {
        await refreshByOwt()
    }
}

onReady.subscribe(async () => {
    await initRefresh()
})
